import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";

import React, { Suspense, useEffect } from "react";
import { useTranslation } from "react-i18next";
import HeadingLayout from "src/components/base/heading-layout";
import { ClockIcon, EllipsisVerticalIcon, UserGroupIcon } from "@heroicons/react/20/solid";
import { ApiInternal } from "src/api/api_internal";
import { SimpleUser } from "src/api/generated/frontend_internal_v1";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import {
    Dropdown,
    DropdownButton,
    DropdownHeading,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
    DropdownSection,
} from "src/components/base/dropdown";
import WS from "src/api/ws";
import ExpireUserDevices from "src/components/dialogs/expire-user-devices";
import TablePagination from "src/components/table-pagination";
import AddToGroupDialog from "src/components/dialogs/add-to-group";
import { useForm } from "@tanstack/react-form";
import { Field, FieldGroup, Fieldset, Label } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import Form from "src/components/base/form";

/**
 * The properties for {@link UserOverview}
 */
export type UserOverviewProps = {};

/** Limit for pagination */
const LIMIT = 20;

/**
 * Overview over all user accounts
 */
function UserOverview(props: UserOverviewProps) {
    const [t] = useTranslation("admin-user-overview");
    const [tg] = useTranslation();

    const navigate = useNavigate();
    const router = useRouter();

    const { page, search } = Route.useSearch();
    const data = Route.useLoaderData();

    const form = useForm({
        defaultValues: {
            search: search,
        },
    });

    const [expireUser, setExpireUser] = React.useState<SimpleUser>();
    const [addToGroup, setAddToGroup] = React.useState<SimpleUser>();

    useEffect(() => {
        const listener = WS.addEventListener("message.UsersChanged", () => {
            router.invalidate({ sync: true });
        });

        return () => WS.removeEventListener(listener);
    }, []);

    return (
        <HeadingLayout heading={t("heading.user-overview")}>
            <Form className={"grid grid-cols-2 gap-12"} onSubmit={form.handleSubmit}>
                <Fieldset>
                    <FieldGroup>
                        <form.Field
                            name={"search"}
                            validators={{
                                onChangeAsync: async ({ fieldApi }) =>
                                    await navigate({
                                        to: "/a/users",
                                        search: {
                                            page: page,
                                            search: fieldApi.state.value,
                                        },
                                    }),
                                onChangeAsyncDebounceMs: 500,
                            }}
                        >
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.filter")}</Label>
                                    <Input
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    />
                                </Field>
                            )}
                        </form.Field>
                    </FieldGroup>
                </Fieldset>
            </Form>
            <Table dense={true}>
                <TableHead>
                    <TableRow>
                        <TableHeader>{t("label.name")}</TableHeader>
                        <TableHeader>{t("label.external-id")}</TableHeader>
                        <TableHeader className={"w-0"}>
                            <span className={"sr-only"}>{tg("accessibility.actions")}</span>
                        </TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.items.map((user) => (
                        <TableRow key={user.uuid} href={"/a/users/$userId/general"} params={{ userId: user.uuid }}>
                            <TableCell>{user.display_name}</TableCell>
                            <TableCell>{user.external_id}</TableCell>
                            <TableCell>
                                <Dropdown>
                                    <DropdownButton plain={true}>
                                        <EllipsisVerticalIcon />
                                    </DropdownButton>
                                    <DropdownMenu anchor={"bottom end"}>
                                        <DropdownSection>
                                            <DropdownItem onClick={() => setAddToGroup(user)}>
                                                <UserGroupIcon />
                                                <DropdownLabel>{t("button.add-to-group")}</DropdownLabel>
                                            </DropdownItem>
                                        </DropdownSection>
                                        <DropdownSection>
                                            <DropdownHeading>{t("heading.danger-zone")}</DropdownHeading>

                                            <DropdownItem onClick={() => setExpireUser(user)}>
                                                <ClockIcon />
                                                <DropdownLabel>{t("button.expire-user")}</DropdownLabel>
                                            </DropdownItem>
                                        </DropdownSection>
                                    </DropdownMenu>
                                </Dropdown>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <TablePagination
                currentPage={page}
                maxPages={data.total == 0 ? 1 : Math.ceil(data.total / LIMIT)}
                href={"/a/users"}
                getSearchParams={(newPage: number) => ({ page: newPage, search: search })}
            />

            {expireUser && (
                <Suspense>
                    <ExpireUserDevices
                        onClose={() => setExpireUser(undefined)}
                        onExpire={() => setExpireUser(undefined)}
                        user={expireUser}
                    />
                </Suspense>
            )}

            {addToGroup && (
                <Suspense>
                    <AddToGroupDialog onClose={() => setAddToGroup(undefined)} user={addToGroup} />
                </Suspense>
            )}
        </HeadingLayout>
    );
}

/** The search parameter of the users view */
type UserOverviewSearch = {
    /** The page the user is currently on */
    page: number;
    /** The search term that should be used */
    search: string;
};

export const Route = createFileRoute("/_admin/a/users/")({
    component: UserOverview,
    validateSearch: (search: Record<string, unknown>): UserOverviewSearch => {
        const page = Number(search?.page ?? 1);

        return {
            page: page <= 0 ? 1 : page,
            search: (search.search as string) || "",
        };
    },
    loaderDeps: ({ search: { page, search } }) => ({ page, search }),
    loader: async ({ deps: { page, search } }) =>
        await ApiInternal.users.getAll({
            limit: LIMIT,
            offset: LIMIT * (page - 1),
            filter_name: search,
        }),
});
